import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";  // ✅ Import de useLocation()
import {
  HomeIcon,
  BuildingOffice2Icon,
  ShoppingBagIcon,
  UsersIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  FolderIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import logotype from "../assets/logo_nom_bleu_blanc_800x800.png";

const navItems = [
  { text: "Dashboard", icon: <HomeIcon className="w-6 h-6" />, path: "/dashboard" },
  { text: "Partenaires", icon: <BuildingOffice2Icon className="w-6 h-6" />, path: "/partenaires" },
  { text: "Produits", icon: <ShoppingBagIcon className="w-6 h-6" />, path: "/produits" },
];

const clientsSubMenu = [
  { text: "Leads", path: "/prospects/leads" },
  { text: "Prospection", path: "/prospects/prospects" },
  { text: "Familles", path: "/prospects/familles" },
];

const toolsSubMenu = [
  { text: "Quantalys", path: "/outils/quantalys" },
  { text: "Simulateur Impôts", path: "/outils/impots" },
  { text: "Mailing", path: "/outils/brevo" },
  { text: "Allocation", path: "/outils/allocation" },
];

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const location = useLocation(); // ✅ Détecte la page actuelle
  const [openTools, setOpenTools] = useState(false);
  const [openClients, setOpenClients] = useState(false);

  // ✅ Ferme le sous-menu "Outils" si l'utilisateur navigue ailleurs
  useEffect(() => {
    if (!toolsSubMenu.some(({ path }) => location.pathname === path)) {
      setOpenTools(false);
    }
    if (!clientsSubMenu.some(({ path }) => location.pathname === path)) {
      setOpenClients(false);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Overlay en mobile */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white text-gray-800 w-63 transform transition-transform 
          border-r border-gray-200 
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          md:translate-x-0 md:static z-50 rounded-br-[120px] overflow-hidden`}
      >

        {/* HEADER : Logo centré */}
        <div className="flex flex-col items-center p-4 transform translate-x-[-5px]">
          <img src={logotype} alt="Logo" style={{ height: "70px", width: "auto" }} className="mb-0" />
        </div>

        {/* NAVIGATION */}
        <nav>
          <ul>
            {navItems.map(({ text, icon, path }) => {
              const isActive = location.pathname === path;
              return (
                <li key={text}>
                  <Link
                    to={path}
                    className={`flex items-center px-6 py-3 rounded-lg mx-2 transition duration-200
                      ${isActive ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
                  >
                    {icon}
                    <span className="ml-3">{text}</span>
                  </Link>
                </li>
              );
            })}

            {/* SOUS-MENU CLIENTS */}
            <li>
              <button
                onClick={() => setOpenClients(!openClients)}
                className={`flex items-center w-full px-6 py-3 rounded-lg mx-2 transition duration-200 
                  ${openClients ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
              >
                <UserGroupIcon className="w-6 h-6" />
                <span className="ml-3 flex-1 text-left">Clients</span>
              </button>

              {/* Sous-menu Clients avec Animation */}
              <ul
                className={`ml-6 border-l border-gray-300 transition-all duration-600 ease-in-out transform 
                  ${openClients ? "scale-y-100 opacity-100 h-auto" : "scale-y-0 opacity-0 h-0"} origin-top overflow-hidden`}
              >
                {clientsSubMenu.map(({ text, path }) => {
                  const isActive = location.pathname === path;
                  return (
                    <li key={text}>
                      <Link
                        to={path}
                        className={`block px-6 py-2 transition duration-200
                          ${isActive ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-200"}`}
                      >
                        {text}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>

            {/* CALENDRIER */}
            <li>
              <Link
                to="/calendrier"
                className={`flex items-center px-6 py-3 rounded-lg mx-2 transition duration-200
                  ${location.pathname === "/calendrier" ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
              >
                <CalendarDaysIcon className="w-6 h-6" />
                <span className="ml-3">Calendrier</span>
              </Link>
            </li>

            {/* FICHIERS & DOCUMENTATION */}
            <li>
              <Link
                to="/fichiers"
                className={`flex items-center px-6 py-3 rounded-lg mx-2 transition duration-200
                  ${location.pathname === "/fichiers" ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
              >
                <FolderIcon className="w-6 h-6" />
                <span className="ml-3">Fichiers</span>
              </Link>
            </li>

            <li>
              <Link
                to="/documents"
                className={`flex items-center px-6 py-3 rounded-lg mx-2 transition duration-200
                  ${location.pathname === "/documents" ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
              >
                <DocumentTextIcon className="w-6 h-6" />
                <span className="ml-3">Documentation</span>
              </Link>
            </li>

            {/* SOUS-MENU OUTILS */}
            <li>
              <button
                onClick={() => setOpenTools(!openTools)}
                className={`flex items-center w-full px-6 py-3 rounded-lg mx-2 transition duration-200 
                  ${openTools ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-800 hover:bg-gray-200"}`}
              >
                <WrenchScrewdriverIcon className="w-6 h-6" />
                <span className="ml-3 flex-1 text-left">Outils</span>
              </button>

              {/* Sous-menu Outils avec Animation */}
              <ul
                className={`ml-6 border-l border-gray-300 transition-all duration-300 ease-in-out transform 
                  ${openTools ? "scale-y-100 opacity-100 h-auto" : "scale-y-0 opacity-0 h-0"} origin-top overflow-hidden`}
              >
                {toolsSubMenu.map(({ text, path }) => {
                  const isActive = location.pathname === path;
                  return (
                    <li key={text}>
                      <Link
                        to={path}
                        className={`block px-6 py-2 transition duration-200
                          ${isActive ? "bg-gray-300 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-200"}`}
                      >
                        {text}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          </ul>
        </nav>

        {/* FOOTER SUPPORT - Centré avec bas arrondi */}
        <div className="absolute bottom-0 w-full bg-white p-8 rounded-br-[120px] text-center shadow-md">
          <Link to="/support" className="flex items-center justify-start text-gray-800 hover:text-gray-600 transition duration-200 ml-4">
            <WrenchScrewdriverIcon className="w-6 h-6 text-gray-600 mr-2" />
            <span className="font-bold text-sm">SUPPORT</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
